import { alpha } from '@mui/material';
import { ChipProps } from '@/components/Chip';
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    default: true;
    primary: true;
    secondary: true;
    success: true;
    error: true;
    info: true;
    warning: true;
  }
  interface ChipPropsVariantOverrides {
    filled: true;
    outlined: true;
    default: true;
  }
  interface ChipPropsSizeOverrides {
    small: true;
    medium: true;
    large: true;
  }
}

export default {
  components: {
    styleOverrides: {
      root: ({ theme }: any) => ({
        display: 'inline-flex',
        gap: theme.spacing(1.5),
        borderRadius: 6,
        fontWeight: 600,
        fontSize: 14
      }),
      label: ({ theme }: any) => ({
        overflow: 'visible',
        position: 'relative' as const,
        padding: theme.spacing(0)
      })
    },
    variants: [
      {
        props: { variant: 'default' } as ChipProps,
        style: ({ theme, ownerState, ...rest }: any) => {
          return {
            border: 'unset',
            backgroundColor: 'unset',
            color: ownerState?.labelColor
              ? ownerState?.labelColor
              : theme.palette.textColor.A60
          };
        }
      },
      {
        props: { variant: 'filled', mode: 'normal' } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          color: ownerState?.labelColor
            ? ownerState?.labelColor
            : theme.palette.base.A0,
          backgroundColor: theme.palette[ownerState?.color]?.A100
        })
      },
      {
        props: {
          variant: 'filled',
          mode: 'normal',
          color: 'secondary'
        } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          color: ownerState?.labelColor
            ? ownerState?.labelColor
            : theme.palette.base.A0,
          backgroundColor: theme.palette.base?.A100
        })
      },
      {
        props: { variant: 'filled', mode: 'light' } as ChipProps,
        style: ({ theme, ...other }: any) => {
          const ownerState = other.as ? other.ownerState : other;
          return {
            color: ownerState?.labelColor
              ? ownerState?.labelColor
              : theme.palette.textColor.A60,
            backgroundColor:
              theme.palette.mode === 'light'
                ? theme.palette[ownerState?.color].A20
                : alpha(theme.palette[ownerState?.color].A100, 0.1)
          };
        }
      },
      {
        props: {
          variant: 'filled',
          mode: 'light',
          color: 'secondary'
        } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          color: ownerState?.labelColor
            ? ownerState?.labelColor
            : theme.palette.mode === 'light'
            ? theme.palette.textColor.A40
            : theme.palette.grayBackground.A20,
          backgroundColor:
            theme.palette.mode === 'light'
              ? alpha(theme.palette.grayBackground.A80, 0.1)
              : alpha(theme.palette.grayBackground.A20, 0.1)
        })
      },
      {
        props: { variant: 'outlined' } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          color: ownerState?.labelColor
            ? ownerState?.labelColor
            : theme.palette.textColor.A60,
          border: `2px solid ${theme.palette[ownerState?.color]?.A60}`
        })
      },
      {
        props: {
          variant: 'outlined',
          color: 'secondary',
          mode: 'normal'
        } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          color: ownerState?.labelColor
            ? ownerState?.labelColor
            : theme.palette.textColor.A60,
          border: `2px solid ${theme.palette.base?.A100}`
        })
      },
      {
        props: {
          variant: 'outlined',
          color: 'secondary',
          mode: 'light'
        } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          color: ownerState?.labelColor
            ? ownerState?.labelColor
            : theme.palette.textColor.A60,
          border: `2px solid ${theme.palette.secondary?.A100}`
        })
      },
      {
        props: { size: 'small', hasBullet: true } as ChipProps,
        style: ({ theme }: any) => ({
          height: 24,
          padding: theme.spacing(0.5, 2, 0.5, 4.5),
          fontSize: 12
        })
      },
      {
        props: { size: 'medium', hasBullet: true } as ChipProps,
        style: ({ theme }: any) => ({
          height: 28,
          padding: theme.spacing(0.5, 2, 0.5, 4.5),
          fontSize: 14
        })
      },
      {
        props: { size: 'large', hasBullet: true } as ChipProps,
        style: ({ theme }: any) => ({
          height: 32,
          padding: theme.spacing(1, 2, 1, 4.5),
          fontSize: 14
        })
      },
      {
        props: { size: 'small', hasBullet: false } as ChipProps,
        style: ({ theme }: any) => ({
          height: 24,
          padding: theme.spacing(0.5, 2),
          fontSize: 12
        })
      },
      {
        props: { size: 'medium', hasBullet: false } as ChipProps,
        style: ({ theme }: any) => ({
          height: 28,
          padding: theme.spacing(0.5, 2),
          fontSize: 14
        })
      },
      {
        props: { size: 'large', hasBullet: false } as ChipProps,
        style: ({ theme }: any) => ({
          height: 32,
          padding: theme.spacing(1, 2),
          fontSize: 14
        })
      },
      {
        props: { hasBullet: true } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          '& > .MuiChip-label	': {
            '&::before': {
              content: '""',
              position: 'absolute',
              height: 6,
              width: 6,
              margin: theme.spacing(0),
              borderRadius: '50%',
              top: 'calc(50% - 3px)',
              left: '-10px',
              backgroundColor: ownerState?.bulletColor
                ? ownerState?.bulletColor
                : theme.palette[ownerState?.color]?.A100
            }
          }
        })
      },
      {
        props: { hasBullet: false } as ChipProps,
        style: {
          '& > .MuiChip-label	': {
            '&::before': {
              display: 'none'
            }
          }
        }
      },
      {
        props: {
          hasBullet: true,
          variant: 'filled',
          mode: 'normal'
        } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          '& > .MuiChip-label	': {
            '&::before': {
              backgroundColor: ownerState?.bulletColor
                ? ownerState?.bulletColor
                : theme.palette.base.A0
            }
          }
        })
      },
      {
        props: {
          hasBullet: true,
          variant: 'filled',
          mode: 'light',
          color: 'secondary'
        } as ChipProps,
        style: ({ theme, ...ownerState }: any) => ({
          '& > .MuiChip-label	': {
            '&::before': {
              backgroundColor: ownerState?.bulletColor
                ? ownerState?.bulletColor
                : theme.palette.base?.A100
            }
          }
        })
      }
    ]
  }
};
